import { produce } from 'immer';
import { SetState, create } from 'zustand';
import '~/types';
import {
  GuessingStep,
  MysteryChatActions,
  MysteryChatModalState,
  MysteryChatStepState,
  MysteryChatStore,
  ChatroomStep,
} from '~/types';

const saveChatroomsToLocalStorage = (chatrooms: ChatroomStep[]) => {
  localStorage.setItem('chatrooms', JSON.stringify(chatrooms));
};

const loadChatroomsFromLocalStorage = (): ChatroomStep[] => {
  const storedChatrooms = localStorage.getItem('chatrooms');
  return storedChatrooms ? JSON.parse(storedChatrooms) : [];
};

const initialModalState = (): MysteryChatModalState => ({
  showCaseNotes: false,
  showChatHistory: false,
});

// Initial chatroom step state, hydrated from localStorage
const initialChatrooms: ChatroomStep[] = loadChatroomsFromLocalStorage();

const MysteryChatStepActions = (
  set: SetState<MysteryChatStore>,
): MysteryChatActions => ({
  setStep: (id: string, step: GuessingStep) => {
    set(
      produce((state: MysteryChatStore) => {
        const chatroom = state.chatrooms.find((room) => room.id === id);
        if (chatroom) {
          chatroom.step = step;
          saveChatroomsToLocalStorage(state.chatrooms);
        }
      }),
    );
  },
  addStep: (id: string, step: GuessingStep) => {
    set(
      produce((state: MysteryChatStore) => {
        state.chatrooms.push({ id, step });
        saveChatroomsToLocalStorage(state.chatrooms);
      }),
    );
  },
  removeStep: (id: string) => {
    set(
      produce((state: MysteryChatStore) => {
        state.chatrooms = state.chatrooms.filter((room) => room.id !== id);
        saveChatroomsToLocalStorage(state.chatrooms);
      }),
    );
  },
  getStep: (id: string): GuessingStep | undefined => {
    let step: GuessingStep | undefined;
    set(
      produce((state: MysteryChatStore) => {
        const chatroom = state.chatrooms.find((room) => room.id === id);
        step = chatroom ? chatroom.step : undefined;
      }),
    );
    return step;
  },
  setShowCaseNotes: (showCaseNotes: boolean) => {
    set(
      produce((state: MysteryChatModalState) => {
        state.showCaseNotes = showCaseNotes;
      }),
    );
  },
});

const useMysteryChatStore = create<MysteryChatStore>((set) => ({
  chatrooms: initialChatrooms,
  ...initialModalState(),
  ...MysteryChatStepActions(set),
}));

export default useMysteryChatStore;
